import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CookieService } from 'ngx-cookie-service';
import { WindowRefService } from '../../../services/window-ref.service';
import { AuthEventRepo, AuthEventType } from '../../../enums/event/event.enum';
import { AuthLinkType } from '../../../enums/event/event-data.enum';
import { LinkClick, LinkClickCustomerPortal, SeenOfferPlacement } from '../../../models/event-data.model';
import {
  AuthEnterpriseEvent,
  EventTrackingService,
  SiteSection,
} from '../../../services/event-tracking.service';
import { Offer, OffersSource } from '../models/offers.model';
import { OffersDisclosureComponent } from '../components/offers-disclosure/offers-disclosure.component';
import { catchError, throwError } from 'rxjs';

export type OfferPlacement = '_HERO' | '_SECONDARY' | '_CAROUSEL' | '_RESUME';

@Injectable({
  providedIn: 'root',
})
export class OffersService {
  constructor(
    private httpClient: HttpClient,
    private cookieService: CookieService,
    private dialog: MatDialog,
    private eventTrackingService: EventTrackingService,
    private windowRefService: WindowRefService
  ) {}

  getOffers(source: OffersSource) {
    return this.httpClient
      .get<Offer[]>(`${environment.customerGateway}/offers?source=${source}`, {
        headers: { Authorization: this.cookieService.get('X-Access-Token') },
      })
      .pipe(

        catchError((error) => {
          console.error('Error fetching offers', error);
          return throwError(() => error);
        })
      );
  }

  getEventData(offer: Offer, siteSection: SiteSection = 'offers', cidOverride?: string): AuthEnterpriseEvent {
    const url = new URL(offer.offerUrl);
    return {
      siteSection,
      crossBuyOfferType: offer.offerType,
      cid: cidOverride ?? url.searchParams.get('cid') ?? this.cookieService.get('cid'),
      utmMedium: url.searchParams.get('utm_medium'),
      utmSource: url.searchParams.get('utm_source'),
      utmCampaign: url.searchParams.get('utm_campaign'),
      customerPortalDisplayText: offer.heading,
      loanleadid: url.searchParams.get('lid'),
      eventRepo: 'customerPortal',
      campaignParticipantCode: url.searchParams.get('cpid'),
    };
  }

  // seenEvent for offers
  trackSeenEvent(options: {
    offer: Offer;
    placement: OfferPlacement;
    siteSection?: SiteSection;
    cid?: string;
    displayTypeSuffix?: string;
  }): void {
    const { offer, placement, siteSection = 'offers', cid, displayTypeSuffix } = options;
    const eventData = this.getEventData(offer, siteSection, cid);
    eventData.product = 'PERSONAL_LOAN';
    const config = offer.offerKey;
    const displayPlacement = placement === '_RESUME' ? '_HERO' : placement;
    eventData.customerPortalDisplayType = `${config}${displayPlacement}${displayTypeSuffix ?? ''}`;
    eventData.subProduct = offer.subProductType;

    this.eventTrackingService.trackEvent<SeenOfferPlacement>(
      AuthEventType.SEEN_OFFER_PLACEMENT,
      eventData as SeenOfferPlacement
    );
  }

  trackLinkClickEvent(options: {
    offer: Offer;
    placement: OfferPlacement;
    siteSection?: SiteSection;
    fsEvent?: string;
    cid?: string;
    displayTypeSuffix?: string;
  }): void {
    const { offer, placement, siteSection = 'offers', fsEvent, cid, displayTypeSuffix } = options;
    if (fsEvent) this.windowRefService.nativeWindow.FS.event(fsEvent);
    const config = offer.offerKey;
    const displayPlacement = placement === '_RESUME' ? '_HERO' : placement;
    const linkType = `${config}${displayPlacement}${displayTypeSuffix ?? ''}`;
    const eventData = this.getEventData(offer, siteSection, cid);
    eventData.customerPortalDisplayText = offer.heading;
    eventData.customerPortalClickLinkText = offer.callToAction ?? offer.label;
    eventData.customerPortalClickLinkType = linkType;
    this.eventTrackingService.trackEvent<LinkClick>(AuthEventType.LINK_CLICK, eventData as LinkClick);
  }

  private getCidFromUrlOrDefault(url: string): string {
    try {
      return new URL(url).searchParams.get('cid') ?? this.cookieService.get('cid');
    } catch {
      return this.cookieService.get('cid');
    }
  }

  trackNoObjectClickEvent(options: {
    url: string;
    config: string;
    placement?: OfferPlacement | '';
    customerPortalClickLinkText: string;
    siteSection?: SiteSection;
  }): void {
    const { url, config, placement = '_SECONDARY', customerPortalClickLinkText, siteSection = 'offers' } = options;
    const linkType = placement === '' ? config : `${config}${placement}`;
    this.eventTrackingService.trackEvent<LinkClick>(AuthEventType.LINK_CLICK, {
      cid: this.getCidFromUrlOrDefault(url),
      eventRepo: 'customerPortal',
      customerPortalClickLinkText,
      customerPortalClickLinkType: linkType,
      siteSection,
    } as unknown as LinkClick);
  }

  trackBefhSeenEvent(
    siteSection: SiteSection,
    options: {
      displayType: 'DebtConsolidation' | 'DebtConsolidationMonthlyPaymentBenefit';
      displayText: string;
    }
  ): void {
    const { displayType, displayText } = options;
    if (!displayType || !displayText) return;

    this.eventTrackingService.trackEvent(AuthEventType.SEEN_BEFH_PLACEMENT, {
      cid: this.cookieService.get('cid'),
      eventRepo: AuthEventRepo.CustomerPortal,
      customerPortalDisplayText: displayText,
      product: 'FINANCIAL_HEALTH',
      customerPortalDisplayType: displayType,
      siteSection,
    });
  }

  trackLearnMoreClick(linkType: AuthLinkType): void {
    this.eventTrackingService.trackEvent<LinkClickCustomerPortal>(AuthEventType.LINK_CLICK, {
      eventRepo: AuthEventRepo.CustomerPortal,
      customerPortalClickLinkText: 'Learn more',
      customerPortalClickLinkType: linkType,
    });
  }

  openDisclosures(subProductType: string | undefined): void {
    this.dialog.open(OffersDisclosureComponent, {
      data: subProductType,
      panelClass: 'disclosure-dialog',
      backdropClass: 'disclosure-backdrop',
    });
  }
}
